import { useState, useCallback, useEffect, useReducer } from "react";
import Image from "next/image";
import { FaPlus, FaMicrophone, FaChevronRight } from "react-icons/fa";
import {} from // useAudioRecorder, // Moved to MicrophoneRecordView
"react-audio-voice-recorder";
import VoiceImage from "@/components/VoiceImage";
import UploadSongView from "./UploadSongView";
import ConversionSongLibrary from "@/components/conversion/ConversionSongLibrary";
import MicrophoneRecordView from "./MicrophoneRecordView"; // Import the new component
import { useRouter } from "next/router";
import axios from "axios";
import { Button, SecondaryButton } from "@jammable/ui-core";
import { Spinner } from "@jammable/ui-components/Loader";
import toast from "react-hot-toast";
import { getAuth } from "firebase/auth";
import VoiceSelector from "./VoiceSelector";
import TextToSpeechInputView from "./TextToSpeechInputView"; // Import the new TTS view
import useSubscription from "@/hooks/useSubscription";
import ConversionSettingsView from "./ConversionSettingsView";
import { BsFillMusicPlayerFill } from "react-icons/bs";
import { IoLibrarySharp, IoSparkles } from "react-icons/io5";
import { MdOutlineTextFields, MdSettings } from "react-icons/md";
import BackButton from "../common/BackButton"; // Import BackButton
import { FaCompactDisc, FaTrash } from "react-icons/fa6";
import { useAuth } from "@/context/AuthProvider";
import VoiceImageNoBackground from "../VoiceImageNoBackground";
import { motion, AnimatePresence } from "framer-motion"; // Import framer-motion
import UpgradeModal from "./UpgradeModal";
import { useModal } from "@/context/ModalContext";
import { rgbDataURL } from "@/utils/tools";
import AudioEffectsSelector from "./AudioEffectsSelector"; // <-- Import the new component
import SubmitSongToLibraryView from "./SubmitSongToLibraryView";
// --- Import Presets --- //
// import { effectPresets } from "./AudioEffectsSelector"; // REMOVE THIS LINE
import { useQuery } from "react-query"; // ADD THIS LINE

const MAX_SONGS = 1; // Maximum number of songs allowed for a mashup

// --- Settings Reducer ---
const initialSettingsState = {
  pitchScaling: {}, // { voiceId: pitchValue, ... }
  addEffects: true,
  voiceVolume: 0,
  extractBackground: false,
  skipExtraction: false,
  highQuality: false,
  name: "",
  autotune: {
    enabled: false,
    strength: 1,
    input_key: "auto",
    subscale: "scale",
    notes_roll: 0,
  },
  selectedTab: "basic",
  selectedEffectId: null, // <-- Reverted to selectedEffectId: "none"
};

console.log(initialSettingsState);

function settingsReducer(state, action) {
  switch (action.type) {
    case "SET_PITCH_SCALING":
      return {
        ...state,
        pitchScaling: {
          ...state.pitchScaling,
          [action.payload.voiceId]: action.payload.pitchValue,
        },
      };
    case "REMOVE_PITCH_SCALING":
      // eslint-disable-next-line no-case-declarations
      const newPitchScaling = { ...state.pitchScaling };
      delete newPitchScaling[action.payload.voiceId];
      return { ...state, pitchScaling: newPitchScaling };

    case "SET_VOICE_VOLUME":
      return { ...state, voiceVolume: action.payload };
    case "SET_EXTRACT_BACKGROUND":
      return { ...state, extractBackground: action.payload };
    case "SET_SKIP_EXTRACTION":
      return { ...state, skipExtraction: action.payload };
    case "SET_HIGH_QUALITY":
      return { ...state, highQuality: action.payload };
    case "SET_NAME":
      return { ...state, name: action.payload };
    case "SET_AUTOTUNE": // Expects payload like { enabled: boolean, strength: number }
      return { ...state, autotune: { ...state.autotune, ...action.payload } };
    case "SET_AUTOTUNE_ENABLED":
      return {
        ...state,
        autotune: { ...state.autotune, enabled: action.payload },
      };
    case "SET_AUTOTUNE_STRENGTH":
      return {
        ...state,
        autotune: { ...state.autotune, strength: action.payload },
      };
    case "SET_SELECTED_TAB":
      return { ...state, selectedTab: action.payload };
    case "SET_AUDIO_EFFECT":
      // payload is now the effectId (string)
      return {
        ...state,
        selectedEffectId: action.payload, // <-- Update selectedEffectId with payload (effectId)
      };
    default:
      console.warn("Unhandled settings action type:", action.type);
      return state;
    // throw new Error(`Unhandled action type: ${action.type}`);
  }
}
// --- End Settings Reducer ---

// --- Animation Variants ---
const modalStepVariants = {
  initial: (direction) => ({
    opacity: 0.7,
    y: direction === "forward" ? 5 : -5,
  }),
  animate: {
    opacity: 1,
    y: 0,
  },
  exit: (direction) => ({
    opacity: 0.7,
    y: direction === "forward" ? -5 : 5,
  }),
  transition: { duration: 0.15, ease: "easeInOut" },
};
// --- End Animation Variants ---

// Base icon component - Moved outside render functions for potential reuse
const BaseIcon = ({ bgColor, IconComponent }) => (
  <div
    className={`tw-flex tw-text-white ${bgColor} tw-items-center tw-justify-center tw-size-10 tw-rounded-xl`}
  >
    <IconComponent size={18} />
  </div>
);

// Source options definition - Moved outside render functions
const sourceOptions = [
  {
    name: "Upload Song",
    sourceKey: "upload",
    mode: "upload",
    color: "bg-green-100", // Keep color for potential use in selection view
    icon: (
      <BaseIcon
        bgColor="tw-bg-green-400"
        IconComponent={BsFillMusicPlayerFill}
      />
    ),
    description: "Upload your own song or audio file!",
  },
  {
    name: "Library",
    sourceKey: "library",
    mode: "library",
    icon: <BaseIcon bgColor="tw-bg-blue-400" IconComponent={IoLibrarySharp} />,
    description: "Select from the Jammable library or your own files!",
  },
  {
    name: "Record",
    sourceKey: "mic",
    mode: "mic",
    icon: <BaseIcon bgColor="tw-bg-red-400" IconComponent={FaMicrophone} />,
    description: "Sing anything with your microphone!",
  },
  {
    name: "Text to Speech",
    sourceKey: "tts",
    mode: "tts",
    icon: (
      <BaseIcon
        bgColor="tw-bg-yellow-400"
        IconComponent={MdOutlineTextFields}
      />
    ),
    description: "Enter the text you want your voice model to say! ",
  },
];

const CreateModal = ({ initialModel = null, onClose }) => {
  const [step, setStep] = useState("selectAction"); // 'selectAction', 'selectSongSource', 'songSourceDetail', 'selectVoices', 'selectSettings', 'selectEffects', 'submitSongToLibrary'
  const [selectedVoices, setSelectedVoices] = useState(
    initialModel ? [initialModel] : [],
  ); // Initialize directly
  const [selectedSongsList, setSelectedSongsList] = useState([]); // { url: string, title: string, metadata?: any, sourceMode: string }[]
  const [selectedSourceMode, setSelectedSourceMode] = useState(null); // <<< New state for mode
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const [initialLibraryTab, setInitialLibraryTab] = useState(null);
  const [viewBeforeSubmit, setViewBeforeSubmit] = useState(null);
  // TODO: Add upgrade modal state if needed for error handling
  // const [upgradeModalOpen, setUpgradeModalOpen] = useState({ isOpen: false, dailyLimit: false });

  // --- State for inline song source detail ---
  const [activeSongSourceDetail, setActiveSongSourceDetail] = useState(null); // 'upload', 'library', 'mic', 'tts', or null

  // --- Use Reducer for Settings ---
  const [settings, dispatchSettings] = useReducer(
    settingsReducer,
    initialSettingsState,
  );
  const [direction, setDirection] = useState("forward"); // Add direction state
  // --- End Use Reducer ---

  // --- Subscription Hook ---
  const { hasCreatorPlan, hasPowerPlan } = useSubscription();
  const notCreator = !hasCreatorPlan() && !hasPowerPlan();
  const { user, updateUser } = useAuth();

  // --- Helper function to determine if user has a paid plan ---
  const hasPaidPlan = () => {
    const quotaConversions = user?.quota?.conversions;
    return quotaConversions?.hasCredits === true;
  };

  // --- Helper function to determine if user is on a free plan ---
  const isFreePlan = () => {
    const quotaConversions = user?.quota?.conversions;
    // True if user does NOT have paid credits but DOES have free credits
    return (
      quotaConversions?.hasCredits === false &&
      quotaConversions?.hasFreeCredits === true
    );
  };

  const quotaConversions = user?.quota?.conversions;

  const userHasPaidPlan = quotaConversions?.hasCredits === true; // Renamed from hasPaidPlan to avoid conflict with helper
  const isUnlimitedPaid =
    userHasPaidPlan && quotaConversions?.remainingCredits === null;
  const isLimitedPaid =
    userHasPaidPlan && typeof quotaConversions?.remainingCredits === "number";
  const isPureFreePlan = !userHasPaidPlan; // User is purely on a free tier

  // --- Helper function to determine credits display ---
  const getCreditsDisplay = () => {
    if (!quotaConversions) return { text: "0", unit: "Credits" }; // Default if no quota info

    // Priority: Paid Plans
    if (isUnlimitedPaid) {
      return { text: "∞", unit: "Credits" };
    }
    if (isLimitedPaid) {
      const credits = quotaConversions?.remainingCredits;
      return { text: `${credits}`, unit: credits === 1 ? "Credit" : "Credits" };
    }

    // Then: Free Plan (only if not on a paid plan)
    if (isPureFreePlan) {
      const remaining = quotaConversions?.remainingFreeCredits ?? 0; // Default to 0 if undefined
      const limit = quotaConversions?.dailyLimit?.limit;
      if (typeof limit === "number") {
        // Display remaining/limit for the free tier
        return { text: `${remaining}/${limit}`, unit: "Daily Limit" };
      } else {
        // Fallback if limit isn't defined in quota, though this shouldn't normally happen for free plans
        console.warn(
          "User has free credits but no daily limit defined in quota.",
        );
        // Display remaining free credits without the limit context
        return { text: `${remaining}`, unit: "Free Credits" };
      }
    }

    // Fallback: No paid or pure free plan detected (e.g., user has no credits at all)
    return { text: "0", unit: "Credits" };
  };
  // --- End Helper functions ---

  const handleGetInTouchClick = () => {
    setViewBeforeSubmit(activeSongSourceDetail);
    setDirection("forward");
    setStep("submitSongToLibrary");
  };

  const handleSelectVoicesAction = () => {
    if (initialModel && selectedVoices.length === 0) {
      setSelectedVoices([initialModel]);
    }
    setDirection("forward"); // Set direction
    setStep("selectVoices"); // Navigate to the new voices step
  };

  const handleSelectSource = (sourceKey, mode) => {
    // Modified to accept mode
    // setSelectedSource(source); // Keep track if needed, but focus on inline detail
    // setStep("songSourceDetail"); // Don't change main step, show inline
    setDirection("forward"); // Set direction
    setActiveSongSourceDetail(sourceKey);
    setSelectedSourceMode(mode); // Set the selected source mode
    setStep("selectAction"); // Navigate back to action step to show inline detail
  };

  const handleBack = () => {
    setDirection("backward"); // Set direction for all back actions
    // If an inline detail view is active, close it first
    if (activeSongSourceDetail) {
      setActiveSongSourceDetail(null);
      setInitialLibraryTab(null);
      // We stay on selectAction, but direction is set to backward
      return; // Stay on the selectAction step
    }

    // Original back logic if no inline detail is open
    switch (step) {
      case "songSourceDetail": // This case might become obsolete if fully inline
        setActiveSongSourceDetail(null); // Ensure inline detail is also closed
        setStep("selectAction"); // Go back to main action selection
        break;
      case "selectSongSource": // New case for the source selection step
      case "selectVoices":
      case "selectSettings":
        setStep("selectAction");
        break;
      default:
        console.warn("Unexpected step in handleBack:", step);
        setStep("selectAction");
        break;
    }
  };

  // --- Callbacks for UploadSongView / Library ---

  // Unified callback for when any source provides song details
  const handleSongSelected = useCallback(
    (songDetails) => {
      // If we are showing the upload view, but the source mode is 'library',
      // it means the user clicked "Get in touch" to submit music.
      if (
        activeSongSourceDetail === "upload" &&
        selectedSourceMode === "library"
      ) {
        toast.success(
          "Thank you for your submission! We will review it shortly.",
        );
        setActiveSongSourceDetail("library"); // Go back to the library view
        return;
      }
      // songDetails: { url: string, title: string, metadata?: any }
      setSelectedSongsList((prevList) => {
        if (prevList.length >= MAX_SONGS) {
          toast.error(`You can select a maximum of ${MAX_SONGS} songs.`);
          return prevList;
        }
        // Add sourceMode to the songDetails before adding to list
        const songWithSourceMode = {
          ...songDetails,
          sourceMode: selectedSourceMode,
        };
        return [...prevList, songWithSourceMode];
      });
      // setSelectedSourceMode is set in handleSelectSource, no need to set it here again
      // unless a specific source type needs to override it AFTER selection
      setActiveSongSourceDetail(null); // Hide the inline detail view
      setStep("selectAction"); // Ensure we are on the main action step
    },
    [selectedSourceMode, activeSongSourceDetail],
  ); // Added selectedSourceMode to dependencies

  // Callback specifically for when TTS text/options are confirmed
  const handleTextConfirmed = useCallback((ttsData) => {
    // ttsData: { text, gender, accent, expression }
    const title = `TTS: "${ttsData.text.substring(0, 30).trim()}...";`;
    setSelectedSongsList((prevList) => {
      if (prevList.length >= MAX_SONGS) {
        toast.error(`You can select a maximum of ${MAX_SONGS} songs.`);
        return prevList;
      }
      const newTtsSong = {
        url: null, // No direct audio URL yet
        title: title,
        metadata: {
          source: "tts", // Keep this for other logic if needed
          ...ttsData, // Store the TTS config here
        },
        sourceMode: "tts", // Explicitly set sourceMode for TTS
      };
      return [...prevList, newTtsSong];
    });
    // setSelectedSourceMode("tts"); // This is already set by handleSelectSource
    setActiveSongSourceDetail(null); // Hide the inline detail view
    setStep("selectAction"); // Ensure we are on the main action step
  }, []); // Removed selectedSourceMode from TTS as it's always 'tts'

  const handleUploadError = (errorMessage, error) => {
    // Handle error, potentially show different UI or toast
    console.error("Upload Error:", errorMessage, error);
    // Toast is already shown in UploadSongView's handleError, but we could add more here
    // Mic loading state is handled within MicrophoneRecordView
    // setMicLoading(false);
    // Optionally close the inline view on error?
    // setActiveSongSourceDetail(null);
  };

  // --- End Callbacks ---

  // --- Conversion Handler ---
  const handleConvert = async () => {
    if (
      loading ||
      !selectedSongsList ||
      selectedSongsList.length === 0 ||
      selectedVoices.length === 0
      // selectedSourceMode is no longer the primary gatekeeper here, as each song has its mode.
      // The main check is selectedSongsList.length > 0
    ) {
      // This toast might be less relevant if the button is disabled correctly.
      // if (!selectedSourceMode && selectedSongsList && selectedSongsList.length > 0) {
      //   toast.error("Please re-select your song source.");
      // }
      return;
    }

    setLoading(true);

    try {
      const auth = getAuth();
      if (!auth.currentUser) {
        throw new Error("User not authenticated."); // Or trigger login flow
      }
      const token = await auth.currentUser.getIdToken();

      // TODO: Backend needs to be updated to process this song_inputs array.
      const firstSongForNaming = selectedSongsList[0]; // Still use first song for default naming
      let conversionName =
        settings.name || firstSongForNaming.title || "Untitled Cover";
      if (selectedSongsList.length > 1 && !settings.name) {
        conversionName += " Mashup";
      }

      const songInputs = selectedSongsList.map((song) => {
        const input = {
          source_mode: song.sourceMode,
          title: song.title, // It might be useful for the backend to have the title too
        };
        if (song.sourceMode === "tts") {
          input.text = song.metadata.text;
          input.gender = song.metadata.gender;
          input.accent = song.metadata.accent;
          input.expression = song.metadata.expression;
        } else {
          input.input_url = song.url;
          input.uploaded_file_id = song.uploaded_file_id || song._id || null;
        }
        return input;
      });

      const res = await axios.post(
        `/api/v2/conversion`,
        {
          name: conversionName,
          voices: selectedVoices.map((v) => ({
            _id: v._id,
            model_id: v.urlParam,
            pitchScaling: settings.pitchScaling[v._id] ?? 0,
          })),
          settings: settings, // This includes selectedEffectId, autotune, etc.
          song_inputs: songInputs, // Array of song inputs
          // input_url and uploaded_file_id are now part of song_inputs
          selected_effect_id: settings.selectedEffectId, // Retained for now, though part of settings
        },
        {
          headers: {
            Authorization: token,
          },
        },
      );

      if (res && res.data?.shortid) {
        // --- Start: Add user update logic ---

        updateUser({
          credits: isFreePlan() ? user.credits : user.credits - 1, // Decrement paid credits if not free
          quota: {
            ...user.quota,
            conversions: {
              ...user.quota.conversions,
              // Only decrement free credits if user is on a free plan
              remainingFreeCredits: isFreePlan()
                ? (user?.quota?.conversions?.remainingFreeCredits ?? 0) - 1
                : user?.quota?.conversions?.remainingFreeCredits,
            },
          },
        });
        // --- End: Add user update logic ---
        router.push(`/conversion/${res.data.shortid}`);
      } else {
        toast.error("Conversion started but failed to get details.");
      }
    } catch (err) {
      // --- Start: Add upgrade modal logic ---
      const errorMessage = err?.response?.data?.message;
      if (errorMessage === "You need to upgrade to use Text to Speech!") {
        if (onClose) onClose(); // Close this modal
        openModal(
          "upgrade-modal",
          <UpgradeModal
            from="create-modal"
            dailyLimit={false}
            message="Upgrade to use Text to Speech!"
          />,
          { className: "tw-p-0" },
        );
      } else if (errorMessage === "You are out of credits") {
        if (onClose) onClose(); // Close modal
        openModal(
          "upgrade-modal",
          <UpgradeModal
            from="create-modal"
            dailyLimit={false}
            message="Upgrade to continue!"
          />,
          { className: "tw-p-0" },
        );
      }
      // --- End: Add upgrade modal logic ---
    } finally {
      setLoading(false);
    }
  };
  // --- End Conversion Handler ---

  // --- Voice Selection Handler ---
  const handleVoiceSelected = useCallback((voice) => {
    setSelectedVoices((prevSelected) => {
      const isSelected = prevSelected.some((v) => v._id === voice._id);
      const MAX_VOICES = 6; // Define the maximum number of voices allowed
      let newSelected;

      if (isSelected) {
        // If already selected, remove it
        newSelected = prevSelected.filter((v) => v._id !== voice._id);
        // Dispatch action to remove pitch scaling for the deselected voice
        dispatchSettings({
          type: "REMOVE_PITCH_SCALING",
          payload: { voiceId: voice._id },
        });
      } else {
        // If not selected, check if the limit has been reached
        if (prevSelected.length >= MAX_VOICES) {
          // If limit reached, show a toast notification and do not add the voice
          toast.error(`You can select a maximum of ${MAX_VOICES} voices.`);
          return prevSelected; // Return the current selection without changes
        }
        // If limit not reached, add the new voice
        // Pitch scaling defaults to 0 via reducer/payload logic
        newSelected = [...prevSelected, voice]; // Just add the voice object
      }
      return newSelected;
    });
  }, []); // Keep dependencies minimal
  // --- End Voice Selection Handler ---

  // --- CSS Class Constants for Placeholders ---
  const placeholderCircleBaseClasses =
    "tw-absolute tw-top-1/2 tw-left-1/2 tw--translate-x-1/2 tw--translate-y-1/2 tw-bg-white tw-rounded-full tw-flex tw-items-center tw-justify-center tw-shrink-0 tw-border tw-border-gray-200 tw-z-10";
  const placeholderCircleSize = "tw-size-16 md:tw-size-24";
  // const selectedVoiceCircleSize = "tw-size-8 md:tw-size-12"; // Removed unused variable

  // --- Helper function to get card colors based on plan and selection ---
  const getCardColors = () => {
    // Removed hasSelectedVoices parameter
    const planName = user?.quota?.planName;
    const isPaid = hasPaidPlan(); // Use existing helper
    const isFree = isFreePlan(); // Use existing helper

    if (planName === "creator" || planName === "power") {
      // Gold for Creator/Power plans
      return {
        bgColor: "tw-bg-yellow-300",
        textColor: "tw-text-yellow-100",
      };
    } else if (isPaid) {
      // Green for other paid plans (e.g., basic/limited)
      return {
        bgColor: "tw-bg-green-300",
        textColor: "tw-text-green-200",
      };
    } else if (isFree) {
      // Gray for Free plans
      return {
        bgColor: "tw-bg-gray-300",
        textColor: "tw-text-gray-200",
      };
    } else {
      // Default/Fallback to Gray
      return {
        bgColor: "tw-bg-gray-300",
        textColor: "tw-text-gray-200",
      };
    }
  };
  // --- End Helper function ---

  // --- Effect to manage default addEffects for TTS ---
  useEffect(() => {
    if (selectedSourceMode === "tts") {
      // Use selectedSourceMode
    }
    // Only re-run if selectedSourceMode or settings.addEffects changes
  }, [selectedSourceMode]); // Add settings.addEffects dependency

  // --- Helper to render inline detail view ---
  const getTitleForSource = (sourceKey) => {
    const titleMap = {
      upload: "Upload Song",
      mic: "Record Audio",
      library: "Select from Library",
      tts: "Text-to-Speech",
    };
    return titleMap[sourceKey] || "Select Source";
  };

  const renderInlineSongSourceDetail = () => {
    if (!activeSongSourceDetail) return null;

    const inlineBack = () => {
      setActiveSongSourceDetail(null); // Clear the active detail
      setStep("selectSongSource"); // Go back to the source selection grid
    };

    const sourceComponentMap = {
      upload: UploadSongView,
      mic: MicrophoneRecordView,
      library: ConversionSongLibrary,
      tts: TextToSpeechInputView,
    };

    const SourceComponent = sourceComponentMap[activeSongSourceDetail];

    if (!SourceComponent) return null;

    const commonProps = {
      onBack: inlineBack,
      onError: handleUploadError, // Pass error handler
    };

    let specificProps = {};
    switch (activeSongSourceDetail) {
      case "upload":
        specificProps = {
          initialModel,
          onSongSelected: handleSongSelected,
          onGetInTouchClick: handleGetInTouchClick,
        };
        break;
      case "mic":
        specificProps = { onSongSelected: handleSongSelected };
        break;
      case "library":
        specificProps = {
          onSourceSelected: handleSongSelected,
          mode: "light",
          onGetInTouchClick: handleGetInTouchClick,
          initialTab: initialLibraryTab,
        };
        break;
      case "tts":
        specificProps = {
          onTextConfirmed: handleTextConfirmed,
          selectedVoices: selectedVoices,
        };
        break;
      default:
        break; // Should not happen
    }

    return (
      <div>
        {/* Unified Header */}
        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
          <BackButton
            onClick={inlineBack}
            aria-label={`Back from ${getTitleForSource(activeSongSourceDetail)}`}
          />
          <h2 className="tw-text-lg tw-my-0 tw-tracking-widest tw-font-black tw-text-black/40 tw-uppercase">
            {getTitleForSource(activeSongSourceDetail)}
          </h2>
        </div>
        {/* Render the specific source component */}
        <SourceComponent {...commonProps} {...specificProps} />
      </div>
    );
  };
  // --- End Helper ---

  const { openModal } = useModal();
  const rgb =
    selectedSongsList && selectedSongsList.length > 0
      ? selectedSongsList[0]?.metadata?.image?.colorPalette?.Vibrant.rgb
      : null; // Updated

  const defaultRGB = [0, 0, 0]; // default to white color

  const safeRGB = (rgb) => {
    if (!rgb || rgb.length !== 3) {
      return defaultRGB;
    }
    return rgb;
  };
  const renderSelectActionView = () => {
    const songOption = (
      <div className="tw-flex tw-flex-col tw-gap-2 tw-items-center tw-truncate tw-text-wrap tw-w-full tw-rounded-xl">
        <div
          className="tw-w-max tw-rounded-2xl tw-flex tw-items-center tw-justify-center tw-relative tw-overflow-hidden"
          // Make the container clickable to add more songs if limit not reached
          onClick={() => {
            if (selectedSongsList.length < MAX_SONGS) {
              setStep("selectSongSource");
            }
          }}
        >
          {selectedSongsList && selectedSongsList.length > 0 ? (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Prevent parent onClick if trash is clicked
                  setSelectedSongsList([]);
                  setSelectedSourceMode(null);
                }}
                className="tw-absolute tw-border-none tw-top-2 tw-right-2 tw-z-10 tw-size-10 tw-bg-white/70 hover:tw-bg-white tw-rounded-full tw-text-gray-600 hover:tw-text-black tw-transition tw-cursor-pointer tw-flex tw-items-center tw-justify-center"
                aria-label="Change song"
              >
                <FaTrash size={16} />
              </button>
              {/* Display for selected songs: count or first song's image */}
              <div
                className={`tw-relative tw-size-32 md:tw-size-56 tw-rounded-2xl tw-overflow-hidden tw-flex tw-items-center tw-justify-center tw-bg-gray-100 ${selectedSongsList.length < MAX_SONGS ? "tw-cursor-pointer hover:tw-bg-gray-200" : ""} tw-transition`}
              >
                {selectedSongsList.length === 1 &&
                selectedSongsList[0].metadata?.image?.url ? (
                  <Image
                    src={selectedSongsList[0].metadata.image.url}
                    alt={selectedSongsList[0].title || "Song cover"}
                    layout="fill"
                    objectFit="cover"
                    sizes="(min-width: 768px) 224px, 160px"
                    blurDataURL={rgbDataURL(
                      safeRGB(rgb)[0],
                      safeRGB(rgb)[1],
                      safeRGB(rgb)[2],
                    )}
                    placeholder="blur"
                  />
                ) : selectedSongsList.length === 1 &&
                  !selectedSongsList[0].metadata?.image?.url ? (
                  // Placeholder for a single selected song without an image
                  <FaCompactDisc className="tw-text-gray-400" size={60} />
                ) : (
                  // Display for multiple songs or if the single song has no image and we opt for count
                  <div className="tw-text-center tw-p-2">
                    <div className="tw-text-2xl md:tw-text-3xl tw-font-bold tw-text-gray-700">
                      {selectedSongsList.length} Song
                      {selectedSongsList.length > 1 ? "s" : ""}
                    </div>
                    <div className="tw-text-sm tw-text-gray-500">
                      {selectedSongsList.length < MAX_SONGS
                        ? "Click to add more"
                        : "Ready for mashup"}
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            // Placeholder when no song is selected - styled like voicesOption placeholder
            <div
              onClick={() => setStep("selectSongSource")} // Navigate to source selection step
              className="tw-relative tw-bg-gray-100 tw-size-32 md:tw-size-56 tw-rounded-2xl tw-flex tw-items-center tw-justify-center tw-cursor-pointer hover:tw-bg-gray-200 tw-transition"
            >
              <div
                className={`${placeholderCircleBaseClasses} ${placeholderCircleSize}`}
              >
                <FaPlus className="tw-text-gray-300 tw-size-6 md:tw-size-8" />
              </div>
            </div>
          )}
        </div>
        <div className="tw-w-full tw-truncate">
          <div className="tw-text-xl tw-text-black tw-font-bold tw-text-center tw-w-full tw-truncate">
            Audio to sing
          </div>
          <div className="tw-text-sm tw-text-gray-500 tw-text-wrap tw-text-center">
            {selectedSongsList && selectedSongsList.length > 0
              ? selectedSongsList.length === 1
                ? selectedSongsList[0].title
                : `${selectedSongsList.length} songs ready for mashup`
              : "Select, sing, type or upload your audio!"}
          </div>
        </div>
      </div>
    );

    const voicesOption = (
      <div className="tw-flex tw-flex-col tw-gap-2 tw-items-center tw-truncate tw-text-wrap tw-w-full tw-rounded-lg">
        <div
          onClick={handleSelectVoicesAction} // Use handler
          className="tw-relative tw-bg-gray-100 tw-size-32 md:tw-size-56 tw-rounded-full tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-2 tw-p-4 tw-overflow-hidden tw-cursor-pointer hover:tw-bg-gray-200 tw-transition"
        >
          {selectedVoices.length > 0 ? (
            selectedVoices.map((voice, index) => {
              // Adjust size: Min 50, max ~80 (range 31)
              const size = 65 + ((index * 25) % 15);
              // Adjust position: Range 25% to 75% (range 51)
              const top = 25 + ((index * 25) % 51);
              const left = 25 + ((index * 30) % 51);

              const shadowSize = 4;
              const shadowColor = `white`;
              const randomRotation = (Math.random() - 0.5) * 20; // Range: -4 to 4 degrees

              return (
                <div
                  key={voice._id}
                  className="tw-absolute"
                  style={{
                    top: `${top}%`,
                    left: `${left}%`,
                    width: `${size}px`,
                    height: `${size}px`,
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  <VoiceImageNoBackground
                    model={voice}
                    size={size}
                    className={``}
                    style={{
                      filter: `
                  drop-shadow(${shadowSize}px 0 0px ${shadowColor})
                  drop-shadow(-${shadowSize}px 0 0px ${shadowColor})
                  drop-shadow(0 ${shadowSize}px 0px ${shadowColor})
                  drop-shadow(0 -${shadowSize}px 0px ${shadowColor})
                `,
                      transform: `rotate(${randomRotation}deg)`,
                    }}
                  />
                </div>
              );
            })
          ) : initialModel ? (
            <VoiceImage
              model={initialModel}
              size={112}
              className="tw-rounded-full tw-shrink-0"
            />
          ) : null}
          <div
            className={`${placeholderCircleBaseClasses} ${placeholderCircleSize}`}
          >
            <FaPlus className="tw-text-gray-300 tw-size-6 md:tw-size-8" />
          </div>
        </div>
        <div>
          <div className="tw-text-xl tw-text-black tw-font-bold tw-text-center">
            Voices to convert
          </div>
          <div className="tw-text-sm tw-text-gray-500 tw-truncate tw-text-wrap tw-text-center">
            {selectedVoices.length > 0
              ? `${selectedVoices.length} voice${selectedVoices.length > 1 ? "s" : ""} selected`
              : "Select voices for the conversion"}
          </div>
        </div>
      </div>
    );

    // --- NEW: Define the Audio Effects option ---
    const effectsOption = (
      <div
        onClick={() => {
          setDirection("forward");
          setStep("selectEffects");
        }}
        className={`tw-relative tw-w-full tw-h-16 md:tw-h-20 tw-rounded-2xl tw-flex tw-items-center tw-justify-between tw-p-4 tw-cursor-pointer hover:tw-brightness-95 tw-transition ${
          settings.selectedEffectId && settings.selectedEffectId !== "none"
            ? effectPresets?.find((p) => p._id === settings.selectedEffectId)
                ?.theme?.backgroundColor || "tw-bg-gray-100"
            : "tw-bg-gray-100"
        }`}
      >
        {/* Left side: Icon and Text */}
        <div className="tw-flex tw-items-center tw-gap-3">
          <div
            className={`tw-rounded-2xl tw-flex tw-items-center tw-justify-center tw-shrink-0 tw-border tw-border-gray-200 tw-size-10 md:tw-size-12 tw-p-2 ${
              settings.selectedEffectId && settings.selectedEffectId !== "none"
                ? "tw-bg-black/5"
                : "tw-bg-white"
            }`}
          >
            {settings.selectedEffectId &&
            settings.selectedEffectId !== "none" &&
            effectPresets?.find((p) => p._id === settings.selectedEffectId)
              ?.emoji ? (
              <span
                className={`tw-text-xl tw-flex ${
                  settings.selectedEffectId &&
                  settings.selectedEffectId !== "none" &&
                  effectPresets?.find(
                    (p) => p._id === settings.selectedEffectId,
                  )?.theme?.backgroundColor
                    ? effectPresets
                        ?.find((p) => p._id === settings.selectedEffectId)
                        .theme.backgroundColor.replace("bg-", "text-")
                    : settings.selectedEffectId &&
                        settings.selectedEffectId !== "none" &&
                        effectPresets?.find(
                          (p) => p._id === settings.selectedEffectId,
                        )?.theme?.textColor
                      ? "tw-text-gray-100"
                      : ""
                }`}
              >
                {
                  effectPresets?.find(
                    (p) => p._id === settings.selectedEffectId,
                  )?.emoji
                }
              </span>
            ) : (
              <IoSparkles
                className={`tw-size-5 md:tw-size-6 ${
                  settings.selectedEffectId &&
                  settings.selectedEffectId !== "none" &&
                  effectPresets?.find(
                    (p) => p._id === settings.selectedEffectId,
                  )?.theme?.backgroundColor
                    ? effectPresets
                        ?.find((p) => p._id === settings.selectedEffectId)
                        .theme.backgroundColor.replace("bg-", "text-")
                    : settings.selectedEffectId &&
                        settings.selectedEffectId !== "none" &&
                        effectPresets?.find(
                          (p) => p._id === settings.selectedEffectId,
                        )?.theme?.textColor
                      ? "tw-text-gray-100"
                      : "tw-text-gray-300"
                }`}
              />
            )}
          </div>
          {/* Text */}
          <div className="tw-flex tw-flex-col tw-text-left">
            <div
              className={`tw-text-base md:tw-text-lg tw-font-bold ${
                settings.selectedEffectId &&
                settings.selectedEffectId !== "none"
                  ? effectPresets?.find(
                      (p) => p._id === settings.selectedEffectId,
                    )?.theme?.textColor || "tw-text-black"
                  : "tw-text-black"
              }`}
            >
              Add Preset
            </div>
            <div
              className={`tw-text-xs md:tw-text-sm tw-font-semibold tw-opacity-60 tw-truncate ${
                settings.selectedEffectId &&
                settings.selectedEffectId !== "none"
                  ? effectPresets?.find(
                      (p) => p._id === settings.selectedEffectId,
                    )?.theme?.textColor || "tw-text-gray-500"
                  : "tw-text-gray-500"
              }`}
            >
              {getCurrentEffectName()}
            </div>
          </div>
        </div>
        <FaChevronRight className="tw-text-gray-400 tw-size-5" />
      </div>
    );
    // --- END: Audio Effects option ---

    return (
      <div className="tw-bg-neutral-100 tw-rounded-3xl tw-overflow-hidden">
        {/* Top section (Header or Inline View) */}
        <div className="tw-bg-white tw-p-3 md:tw-p-5 tw-pb-3 tw-rounded-b-3xl tw-shadow-b-3xl tw-border tw-border-gray-200 tw-border-solid tw-rounded-t-3xl">
          {activeSongSourceDetail ? (
            // If inline detail is active, render it (includes BackButton + Title)
            renderInlineSongSourceDetail()
          ) : (
            // Otherwise, render the main selection header and grid
            <>
              {/* {isFreePlan() && (
                <div
                  onClick={() => {
                    openModal(
                      "upgrade-modal",
                      <UpgradeModal
                        from="create-modal"
                        dailyLimit={false}
                        message="Join the creator plan to create unlimited covers!"
                      />,
                      { className: "tw-p-0" },
                    );
                  }}
                  className="tw-mb-4 tw-p-3 md:tw-p-3 tw-rounded-3xl tw-cursor-pointer tw-text-white tw-bg-gradient-to-r tw-from-purple-600 tw-via-pink-500 tw-to-red-500 hover:tw-brightness-110 tw-transition-all tw-flex tw-items-center tw-justify-between"
                >
                  <div className="tw-flex tw-flex-col tw-flex-grow">
                    <div className="tw-flex tw-items-center tw-gap-3">
                      <IoSparkles size={28} className="tw-flex-shrink-0" />
                      <div>
                        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">
                          <span className="tw-bg-green-300 tw-text-black tw-text-xs tw-font-black tw-px-2 tw-py-0.5 tw-rounded-lg tw-uppercase tw-tracking-wider tw-flex tw-items-center tw-gap-x-1">
                            <FaFire size={12} />
                            Popular
                          </span>
                          <span className="tw-block tw-font-bold tw-text-lg md:tw-text-xl">
                            Unlock the Creator Plan!
                          </span>
                        </div>
                        <span className="tw-block tw-text-xs sm:tw-text-sm tw-opacity-80 tw-text-wrap">
                          Create <strong>Unlimited</strong> Covers, Train{" "}
                          <strong>5</strong> Voices Every Month, Lyric Videos &
                          more!
                        </span>
                      </div>
                    </div>
                  </div>
                  <FaChevronRight
                    size={22}
                    className="tw-opacity-90 tw-self-center tw-ml-3 md:tw-ml-4"
                  />
                </div>
              )} */}
              <div className="tw-text-xl tw-mb-2 md:tw-mb-0 tw-tracking-widest tw-font-black tw-text-black/40 tw-uppercase ">
                Create Your Song
              </div>
              {/* The grid container class might need adjustment based on content */}
              <div className="tw-flex tw-flex-col tw-truncate tw-gap-2 md:tw-gap-4 tw-items-center tw-justify-center">
                {" "}
                {/* Main container: vertical flex */}
                {/* Row for Song and Voices */}
                <div className="tw-flex tw-flex-row tw-w-full tw-gap-2 md:tw-gap-4 tw-items-start tw-justify-center">
                  {songOption}
                  {voicesOption}
                </div>
                {/* Row for Effects - ensure it can span if needed */}
                <div className="tw-w-full">{effectsOption}</div>
              </div>
            </>
          )}
        </div>

        {/* Conditionally render the footer ONLY when NO inline detail is active */}
        {!activeSongSourceDetail && (
          <div className="tw-flex tw-flex-col tw-gap-2 tw-p-4 md:tw-p-5 tw-py-4">
            {/* {conversionError && (
              <div className="tw-text-red-600 tw-text-sm tw-text-center">
                {conversionError}
              </div>
            )} */}
            <div className="tw-flex tw-items-center md:tw-gap-6 tw-gap-2 ">
              <div className="tw-text-sm tw-text-gray-500 tw-text-center">
                <div className="tw-flex tw-gap-1 md:tw-gap-2 tw-h-auto">
                  <div className="tw-w-10 tw-h-auto tw-relative tw-flex ">
                    {/* Apply conditional styling to background cards using cardColors */}
                    {(() => {
                      const cardColors = getCardColors(); // Get colors once
                      return (
                        <>
                          <div
                            className={`${cardColors.bgColor} tw-h-10 tw-top-1 tw-rounded-lg tw-w-8 tw-left-0 tw-rotate-[10deg] tw-absolute tw-origin-bottom`}
                          />
                          <div
                            className={`${cardColors.bgColor} tw-h-10 tw-top-1 tw-rounded-lg tw-w-8 tw-left-0 tw-rotate-[-10deg] tw-absolute tw-origin-bottom`}
                          />
                          {/* Main card styling using cardColors */}
                          <div
                            className={`tw-w-8 tw-rounded-lg tw-shadow tw-h-auto ${cardColors.bgColor} tw-overflow-hidden tw-relative`}
                          >
                            <div
                              className={`tw-absolute tw-text-7xl tw--top-2 tw--left-0 tw-font-black ${cardColors.textColor} tw-shadow-xl`}
                            >
                              V
                            </div>
                            {/* Conditional "Free" badge on the main card for free plans */}
                            {isFreePlan() && (
                              <div className="tw-absolute tw-bottom-0.5 tw-right-0.5 tw-bg-green-500 tw-text-white tw-text-[8px] tw-font-bold tw-px-1 tw-py-0.5 tw-rounded-md tw-leading-none">
                                FREE
                              </div>
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  <div className="tw-flex tw-flex-col tw-font-bold tw-justify-center tw-gap-0.5">
                    <div className="tw-text-black/40 tw-flex tw-items-center tw-gap-1 tw-justify-center">
                      Available
                    </div>
                    <span className="tw-w-full tw-text-nowrap">
                      <span>{getCreditsDisplay().text} </span>{" "}
                      {getCreditsDisplay().unit}{" "}
                    </span>{" "}
                  </div>
                </div>
              </div>
              <div className="tw-flex  tw-gap-1 md:tw-gap-2 tw-w-full">
                <>
                  <Button
                    onClick={() => {
                      if (selectedSongsList.length === 0) {
                        setDirection("forward");
                        setStep("selectSongSource");
                      } else {
                        handleConvert();
                      }
                    }}
                    disabled={loading}
                    className="tw-font-black tw-uppercase tw-text-base tw-rounded-2xl tw-px-2 tw-w-full tw-h-12 tw-border tw-border-solid tw-border-purple-700 tw-border-b-4 tw-border-b-purple-900 active:tw-translate-y-0.5 active:tw-border-b-2 active:tw-scale-[0.98] tw-transition tw-duration-100 tw-ease-in-out disabled:tw-border-b-4 disabled:tw-border-b-gray-400 disabled:tw-border-gray-300"
                  >
                    {loading ? (
                      <Spinner
                        className="tw-border-white tw-border-3"
                        size={4}
                      />
                    ) : selectedSongsList.length === 0 ? (
                      "Select Song"
                    ) : (
                      <div className="tw-flex tw-items-center tw-gap-2">
                        <IoSparkles
                          size={20}
                          className="tw-hidden sm:tw-block"
                        />
                        Start Cover
                      </div>
                    )}
                  </Button>
                  <SecondaryButton
                    onClick={() => {
                      setDirection("forward"); // Set direction
                      setStep("selectSettings"); // Navigate to settings
                    }}
                    variant="outline"
                    size="icon"
                    aria-label="Conversion Settings"
                    disabled={loading} // Keep original disable logic
                    className="tw-font-bold tw-text-base tw-rounded-2xl tw-size-12 tw-w-12 tw-min-w-12 md:tw-p-0 tw-px-0"
                  >
                    <MdSettings size={20} className="tw-text-purple-700" />
                  </SecondaryButton>
                </>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // --- New function to render the song source selection view ---
  const renderSelectSongSourceView = () => {
    return (
      <div className="tw-bg-white tw-p-5 tw-rounded-3xl tw-shadow-lg tw-border tw-border-gray-200 tw-border-solid">
        {/* Header with Back Button */}
        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-4">
          <BackButton
            onClick={handleBack} // Use existing back handler
            aria-label="Back to main creation step"
          />
          <h2 className="tw-text-lg tw-my-0 tw-tracking-widest tw-font-black tw-text-black/40 tw-uppercase">
            Choose Song Source
          </h2>
        </div>

        {/* Grid of Source Options */}
        <div className="tw-grid tw-grid-cols-2 tw-gap-3">
          {sourceOptions.map((opt) => (
            <div
              key={opt.sourceKey}
              onClick={() => handleSelectSource(opt.sourceKey, opt.mode)} // Pass mode here
              className={`tw-flex tw-flex-col tw-bg-white tw-gap-2 tw-p-3 tw-rounded-2xl hover:tw-bg-gray-100 tw-cursor-pointer tw-transition ${opt.color || "bg-gray-50"} tw-border tw-border-gray-200 tw-border-solid `}
            >
              {opt.icon}
              <span className="tw-text-md tw-font-bold tw-text-gray-700">
                {opt.name}
              </span>
              <span className="tw-text-xs tw-text-gray-500  tw-text-wrap">
                {opt.description}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };
  // --- End new render function ---

  const renderSubmitSongToLibraryView = () => {
    return (
      <SubmitSongToLibraryView
        onBack={() => {
          setDirection("backward");
          setActiveSongSourceDetail(viewBeforeSubmit || "library");
          setStep("selectAction");
        }}
        onSuccess={() => {
          setInitialLibraryTab("my-files");
          setDirection("backward");
          setActiveSongSourceDetail("library");
          setStep("selectAction");
        }}
      />
    );
  };

  // --- Handler for selecting an effect ---
  const handleEffectSelected = useCallback((effectId) => {
    dispatchSettings({ type: "SET_AUDIO_EFFECT", payload: effectId });
    // Optionally navigate back automatically after selection
    // setDirection("backward");
    // setStep("selectAction");
  }, []);

  // Fetch effects using React Query
  const {
    data: effectPresets,
    isLoading: isLoadingEffects,
    error: errorEffects,
  } = useQuery({
    queryKey: ["effectPresets"],
    queryFn: async () => {
      const { data } = await axios.get("/api/effects/getEffects");
      if (!data.success) {
        throw new Error(data.message || "Failed to fetch effects");
      }
      return data.data;
    },
  });

  if (isLoadingEffects) {
    // Optional: You might want to disable the effect selection UI or show a loader within the settings section
    console.log("Effects are loading...");
  }

  if (errorEffects) {
    console.error("Error fetching effects:", errorEffects);
    // Optional: Show an error message to the user within the settings section
  }

  // Helper to get current effect name for display
  const getCurrentEffectName = () => {
    if (!effectPresets || settings.selectedEffectId === "none") {
      return "Optional";
    }
    const effect = effectPresets.find(
      (p) => p._id === settings.selectedEffectId,
    );
    return effect ? effect.name : "Optional";
  };

  // --- Main component return logic based on `step` ---
  return (
    <div className="vertical-flex-10 tw-w-full tw-truncate">
      <AnimatePresence mode="wait" custom={direction}>
        {" "}
        {/* Pass direction */}
        {step === "selectAction" && (
          <motion.div
            key="selectAction"
            custom={direction} // Pass direction
            variants={modalStepVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={modalStepVariants.transition}
            className="tw-w-full" // Add width to ensure layout consistency during transition
          >
            {renderSelectActionView()}
          </motion.div>
        )}
        {step === "selectSongSource" && (
          <motion.div
            key="selectSongSource"
            custom={direction} // Pass direction
            variants={modalStepVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={modalStepVariants.transition}
            className="tw-w-full"
          >
            {renderSelectSongSourceView()}
          </motion.div>
        )}
        {step === "selectVoices" && (
          <motion.div
            key="selectVoices"
            custom={direction} // Pass direction
            variants={modalStepVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={modalStepVariants.transition}
            className="tw-w-full"
          >
            <VoiceSelector
              selectedVoices={selectedVoices}
              onSelectVoice={handleVoiceSelected}
              onBack={handleBack} // handleBack navigates back to selectAction
            />
          </motion.div>
        )}
        {step === "selectSettings" && (
          <motion.div
            key="selectSettings"
            custom={direction} // Pass direction
            variants={modalStepVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={modalStepVariants.transition}
            className="tw-w-full"
          >
            <ConversionSettingsView
              settings={settings}
              dispatchSettings={dispatchSettings}
              selectedVoices={selectedVoices}
              notCreator={notCreator}
              onBack={handleBack} // handleBack navigates back to selectAction
            />
          </motion.div>
        )}
        {/* --- NEW: Add case for selectEffects --- */}
        {step === "selectEffects" && (
          <motion.div
            key="selectEffects"
            custom={direction}
            variants={modalStepVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={modalStepVariants.transition}
            className="tw-w-full"
          >
            <AudioEffectsSelector
              onBack={handleBack} // Use the existing back handler
              selectedEffectId={settings.selectedEffectId} // <-- Pass settings.selectedEffectId
              onSelectEffect={handleEffectSelected} // <-- Pass handleEffectSelected
            />
          </motion.div>
        )}
        {step === "submitSongToLibrary" && (
          <motion.div
            key="submitSongToLibrary"
            custom={direction}
            variants={modalStepVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={modalStepVariants.transition}
            className="tw-w-full"
          >
            {renderSubmitSongToLibraryView()}
          </motion.div>
        )}
        {/* --- END: Add case for selectEffects --- */}
        {/* Add other top-level steps here if needed */}
      </AnimatePresence>
    </div>
  );
};

export default CreateModal;
